// src/pages/creator-panel/Connector/GoogleReviews/ListGoogleReviews.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import DataTableWithFilters from "../../../../components/Common/DataTableWithFilters";
import EditGoogleReview from "./EditGoogleReview";
import {
    Tooltip, Modal as MuiModal, Box, Button as MuiButton, useTheme, Stack,
    FormControl, InputLabel, Select as MuiSelect, MenuItem, Chip, Typography,
    IconButton, Grid, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

// Compact star display: filled stars colored by sentiment, empty stars grey.
const StarRating = ({ value }) => {
    const v = Math.max(0, Math.min(5, value || 0));
    const color = v >= 4 ? "#2e7d32" : v <= 2 ? "#d32f2f" : "#ed6c02";
    return (
        <Box component="span" sx={{ whiteSpace: "nowrap", fontSize: "1.05em" }}>
            <span style={{ color }}>{"★".repeat(v)}</span>
            <span style={{ color: "#cfcfcf" }}>{"☆".repeat(5 - v)}</span>
        </Box>
    );
};

const formatDate = (iso) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
};

const ExpandedComponent = ({ data }) => (
    <Box sx={{ p: 2, backgroundColor: "action.hover", borderLeft: "3px solid", borderColor: "primary.main" }}>
        <Grid container spacing={1.5} sx={{ fontSize: "0.9em" }}>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>DB ID:</strong> {data.id}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Reviewer:</strong> {data.reviewer_display_name || "Anonymous"}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Rating:</strong> {data.star_rating || 0} / 5</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Trained:</strong> {data.is_trained ? "Yes" : "No"}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Manually Edited:</strong> {data.is_manually_edited ? "Yes" : "No"}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Pending Update:</strong> {data.has_pending_update ? "Yes" : "No"}</Typography></Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>Review Comment:</Typography>
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, mt: 0.5, bgcolor: "background.paper" }}>
                    <Typography variant="body2">{data.comment || "(No written comment.)"}</Typography>
                </Box>
            </Grid>

            {data.reply_comment && (
                <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>Owner Reply:</Typography>
                    <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, mt: 0.5, bgcolor: "background.paper" }}>
                        <Typography variant="body2">{data.reply_comment}</Typography>
                    </Box>
                </Grid>
            )}

            <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>Embedded Content (used for chatbot training):</Typography>
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, mt: 0.5, maxHeight: 200, overflowY: "auto", bgcolor: "background.paper", whiteSpace: "pre-wrap" }}>
                    <Typography variant="body2">{data.page_content || "N/A"}</Typography>
                </Box>
            </Grid>

            {data.has_pending_update && data.pending_update_content && (
                <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "warning.main" }}>Pending Update From Google:</Typography>
                    <Box sx={{ p: 1.5, border: "1px solid", borderColor: "warning.light", borderRadius: 1, mt: 0.5, maxHeight: 150, overflowY: "auto", bgcolor: "#fffbe6", whiteSpace: "pre-wrap" }}>
                        <Typography variant="body2">{data.pending_update_content}</Typography>
                    </Box>
                </Grid>
            )}
        </Grid>
    </Box>
);

export default function ListGoogleReviews({ refreshTrigger, chatbotId, permission }) {
    const theme = useTheme();
    const [reviews, setReviews] = useState([]);
    const [filteredReviews, setFilteredReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
    const [currentReview, setCurrentReview] = useState(null);

    // Filters
    const [starFilter, setStarFilter] = useState("all");
    const [trainFilter, setTrainFilter] = useState("all");
    const [replyFilter, setReplyFilter] = useState("all");

    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    const muiModalStyle = {
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: { xs: "95%", sm: "90%", md: "70vw" }, maxWidth: "900px",
        maxHeight: "90vh", bgcolor: "background.paper",
        border: "none", borderRadius: "8px", boxShadow: 24, p: 0,
        display: "flex", flexDirection: "column", outline: "none", overflow: "hidden",
    };

    const fetchReviews = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axiosInstance.get(`/api/google-reviews/synced-reviews`, { params });
            setReviews(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Error fetching synced reviews.");
            setReviews([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatbotId]);

    useEffect(() => { fetchReviews(); }, [refreshTrigger, fetchReviews]);

    useEffect(() => {
        const lower = searchText.toLowerCase();
        const filtered = reviews.filter((r) => {
            const matchesSearch = !searchText || Object.values(r).some((v) => String(v).toLowerCase().includes(lower));
            const matchesStar = starFilter === "all" || String(r.star_rating) === String(starFilter);
            const matchesTrain = trainFilter === "all" || (trainFilter === "trained" ? r.is_trained : !r.is_trained);
            const matchesReply = replyFilter === "all" || (replyFilter === "answered" ? r.has_reply : !r.has_reply);
            return matchesSearch && matchesStar && matchesTrain && matchesReply;
        });
        setFilteredReviews(filtered);
    }, [searchText, reviews, starFilter, trainFilter, replyFilter]);

    const handleTrain = async () => {
        const toTrain = selectedRows.filter((r) => !r.is_trained || r.is_manually_edited);
        if (toTrain.length === 0) {
            setError("No reviews selected, or all selected reviews are already trained.");
            return;
        }
        setIsProcessing(true);
        setError("");
        try {
            const reviewIds = toTrain.map((r) => r.id);
            const response = await axiosInstance.post(`/api/google-reviews/train`, { reviewIds }, { params });
            setError(response.data.message || "Reviews trained successfully.");
            fetchReviews();
            setSelectedRows([]);
            setToggleCleared((p) => !p);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to train reviews.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUntrain = async () => {
        const toUntrain = selectedRows.filter((r) => r.is_trained);
        if (toUntrain.length === 0) {
            setError("No trained reviews selected.");
            return;
        }
        setIsProcessing(true);
        setError("");
        try {
            const reviewIds = toUntrain.map((r) => r.id);
            const response = await axiosInstance.post(`/api/google-reviews/untrain`, { reviewIds }, { params });
            setError(response.data.message || "Reviews removed from knowledge base.");
            fetchReviews();
            setSelectedRows([]);
            setToggleCleared((p) => !p);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to untrain reviews.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = async (reviewId, reviewer) => {
        if (!window.confirm(`Remove the review by "${reviewer || "Anonymous"}" from the synced list?`)) return;
        setActionLoading(`delete-${reviewId}`);
        setError("");
        try {
            await axiosInstance.delete(`/api/google-reviews/delete-review/${reviewId}`, { params });
            setReviews((prev) => prev.filter((r) => r.id !== reviewId));
            setError("Review deleted successfully.");
        } catch (err) {
            setError(err.response?.data?.message || "Error deleting review.");
        } finally {
            setActionLoading(null);
        }
    };

    const openEditModal = (review) => { setCurrentReview(review); setIsEditModalOpen(true); };
    const closeEditModal = () => { setIsEditModalOpen(false); setCurrentReview(null); };
    const handleUpdateFromEdit = () => { fetchReviews(); closeEditModal(); setError("Review updated successfully."); };

    const openPendingModal = (review) => { setCurrentReview(review); setIsPendingModalOpen(true); };
    const closePendingModal = () => { setIsPendingModalOpen(false); setCurrentReview(null); };

    const handleResolvePending = async (action) => {
        if (!currentReview) return;
        setActionLoading(action);
        setError("");
        try {
            const payload = { action };
            if (action === "accept") payload.page_content = currentReview.pending_update_content;
            await axiosInstance.post(`/api/google-reviews/resolve-pending-update/${currentReview.id}`, payload, { params });
            fetchReviews();
            closePendingModal();
            setError(`Pending update ${action === "accept" ? "accepted" : "rejected"}.`);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to resolve pending update.");
        } finally {
            setActionLoading(null);
        }
    };

    const handleRowSelected = ({ selectedRows: rows }) => setSelectedRows(rows);

    const columns = [
        {
            name: "Rating",
            selector: (row) => row.star_rating,
            sortable: true,
            width: "120px",
            cell: (row) => <StarRating value={row.star_rating} />,
        },
        {
            name: "Reviewer",
            selector: (row) => row.reviewer_display_name,
            sortable: true,
            wrap: true,
            cell: (row) => <span>{row.reviewer_display_name || "Anonymous"}</span>,
        },
        {
            name: "Comment",
            selector: (row) => row.comment,
            grow: 2,
            wrap: true,
            cell: (row) => {
                const text = row.comment || "(No written comment.)";
                const short = text.length > 90 ? `${text.slice(0, 90)}...` : text;
                return <Tooltip title={text} arrow><span>{short}</span></Tooltip>;
            },
        },
        {
            name: "Date",
            selector: (row) => row.create_time,
            sortable: true,
            width: "120px",
            cell: (row) => <span>{formatDate(row.create_time)}</span>,
        },
        {
            name: "Reply",
            center: true,
            width: "110px",
            cell: (row) => (
                <Chip
                    label={row.has_reply ? "Replied" : "No reply"}
                    color={row.has_reply ? "success" : "default"}
                    size="small"
                    variant={row.has_reply ? "filled" : "outlined"}
                />
            ),
        },
        {
            name: "Status",
            center: true,
            width: "150px",
            cell: (row) => (
                row.has_pending_update
                    ? <Chip label="Review Pending" color="warning" size="small" onClick={() => openPendingModal(row)} />
                    : <Chip label={row.is_trained ? "Trained" : "Not Trained"} color={row.is_trained ? "success" : "default"} size="small" variant={row.is_trained ? "filled" : "outlined"} />
            ),
        },
        {
            name: "Actions",
            width: "120px",
            cell: (row) => (
                <Box>
                    {permission.canModify && (
                        <Tooltip title="Edit Content">
                            <IconButton size="small" color="primary" onClick={() => openEditModal(row)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {permission.canDelete && (
                        <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id, row.reviewer_display_name)} disabled={actionLoading === `delete-${row.id}`}>
                                {actionLoading === `delete-${row.id}` ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const filterComponents = (
        <>
            <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Rating</InputLabel>
                <MuiSelect value={starFilter} label="Rating" onChange={(e) => setStarFilter(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="5">5 stars</MenuItem>
                    <MenuItem value="4">4 stars</MenuItem>
                    <MenuItem value="3">3 stars</MenuItem>
                    <MenuItem value="2">2 stars</MenuItem>
                    <MenuItem value="1">1 star</MenuItem>
                </MuiSelect>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Trained</InputLabel>
                <MuiSelect value={trainFilter} label="Trained" onChange={(e) => setTrainFilter(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="trained">Yes</MenuItem>
                    <MenuItem value="not_trained">No</MenuItem>
                </MuiSelect>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Reply</InputLabel>
                <MuiSelect value={replyFilter} label="Reply" onChange={(e) => setReplyFilter(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="answered">Replied</MenuItem>
                    <MenuItem value="unanswered">No reply</MenuItem>
                </MuiSelect>
            </FormControl>
        </>
    );

    const selectedRowsActions = (
        <Stack direction="row" spacing={2}>
            {permission.canModify && (
                <>
                    <MuiButton
                        variant="contained" color="primary" size="small"
                        onClick={handleTrain} disabled={isProcessing}
                        startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
                    >
                        {isProcessing ? "Processing..." : "Train"}
                    </MuiButton>
                    <MuiButton
                        variant="outlined" color="warning" size="small"
                        onClick={handleUntrain} disabled={isProcessing}
                        startIcon={<RemoveCircleOutlineIcon />}
                    >
                        Untrain
                    </MuiButton>
                </>
            )}
        </Stack>
    );

    return (
        <Box mt={2}>
            <DataTableWithFilters
                columns={columns}
                data={filteredReviews}
                loading={loading}
                error={error}
                onErrorClose={() => setError("")}
                filterComponents={filterComponents}
                selectedRowsActions={selectedRowsActions}
                selectedRowsCount={selectedRows.length}
                onSelectedRowsChange={handleRowSelected}
                clearSelectedRows={toggleCleared}
                expandableRows
                expandableRowsComponent={ExpandedComponent}
                noDataMessage="No synced reviews found. Click 'Sync Reviews' to pull them from Google."
                theme={theme}
                searchText={searchText}
                onSearchChange={(e) => setSearchText(e.target.value)}
                searchPlaceholder="Search by reviewer, comment, or rating..."
            />

            <MuiModal open={isEditModalOpen} onClose={closeEditModal}>
                <Box sx={muiModalStyle}>
                    {currentReview && (
                        <EditGoogleReview
                            review={currentReview}
                            chatbotId={chatbotId}
                            permission={permission}
                            onClose={closeEditModal}
                            onUpdate={handleUpdateFromEdit}
                        />
                    )}
                </Box>
            </MuiModal>

            <Dialog open={isPendingModalOpen} onClose={closePendingModal} maxWidth="md" fullWidth>
                <DialogTitle>Pending Update From Google</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Your current (edited) content:</Typography>
                    <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 2, maxHeight: 200, overflowY: "auto", whiteSpace: "pre-wrap", bgcolor: "background.paper" }}>
                        <Typography variant="body2">{currentReview?.page_content || "N/A"}</Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, color: "warning.main" }}>Incoming content from Google:</Typography>
                    <Box sx={{ p: 1.5, border: "1px solid", borderColor: "warning.light", borderRadius: 1, maxHeight: 200, overflowY: "auto", whiteSpace: "pre-wrap", bgcolor: "#fffbe6" }}>
                        <Typography variant="body2">{currentReview?.pending_update_content || "N/A"}</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={() => handleResolvePending("reject")} disabled={actionLoading !== null} color="inherit">
                        {actionLoading === "reject" ? "Working..." : "Keep My Edit"}
                    </MuiButton>
                    <MuiButton onClick={() => handleResolvePending("accept")} disabled={actionLoading !== null} variant="contained" color="primary">
                        {actionLoading === "accept" ? "Working..." : "Accept Google's Version"}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
